import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import "./sessions.css";
import Levels from "./components/Levels";
import PreviousSession from "./components/PreviousSession";
import Loader from "../../../components/Loader";
import { API_BASE_URL, authFetch } from "../../../config/api";
import { fetchSessions as fetchSessionsUtil } from "../../../utils/fetchSessions";

const sessionsCache = new Map();
const CACHE_DURATION = 3 * 60 * 1000;

function Sessions() {
  const [showForm, setShowForm] = useState(false);
  const [load, setLoad] = useState(false);
  const [newSession, setNewSession] = useState("");
  const [isCurrent, setIsCurrent] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [otherSessions, setOtherSessions] = useState([]);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchSessions = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && sessionsCache.has("sessions")) {
      const cached = sessionsCache.get("sessions");
      if (now - cached.timestamp < CACHE_DURATION) {
        setCurrentSession(cached.data.current);
        setOtherSessions(cached.data.others);
        return;
      }
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoad(true);
    setError(null);
    try {
      const json = await fetchSessionsUtil();
      if (json.success) {
        const current = json.sessions.find((s) => s.current) || null;
        const others = json.sessions.filter((s) => !s.current);
        setCurrentSession(current);
        setOtherSessions(others);
        sessionsCache.set("sessions", {
          data: { current, others },
          timestamp: now,
        });
        if (!current) setShowForm(true);
      }
    } catch (err) {
      if (err.name !== "AbortError") setError(err.message);
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    return () => abortRef.current?.abort();
  }, [fetchSessions]);

  const registerSession = useCallback(async () => {
    if (!newSession.trim()) return;
    setLoad(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/auth/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session: newSession.trim(),
          current: isCurrent,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      sessionsCache.clear();
      await fetchSessions(true);
      setNewSession("");
      setIsCurrent(false);
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoad(false);
    }
  }, [newSession, isCurrent, fetchSessions]);

  return (
    <div className="cr_page">
      <div className="cr_header">
        <div>
          <p className="cr_overline">Academic Tools</p>
          <h2>Course Registration</h2>
          <p className="cr_subtitle">
            Manage sessions and register courses by level.
          </p>
        </div>
        <button
          className="cr_btn cr_btn--primary"
          onClick={() => setShowForm((v) => !v)}
          disabled={load}
        >
          {showForm ? "Cancel" : "New Session"}
        </button>
      </div>

      {load && <Loader />}

      {error && (
        <div className="cr_error">
          {error} —{" "}
          <button className="cr_retry" onClick={() => fetchSessions(true)}>
            Retry
          </button>
        </div>
      )}

      {showForm && (
        <div className="cr_form">
          <div className="cr_form_field">
            <label>Session</label>
            <input
              type="text"
              placeholder="e.g. 2024-2025"
              value={newSession}
              onChange={(e) => setNewSession(e.target.value)}
              disabled={load}
            />
          </div>
          <label className="cr_checkbox_label">
            <input
              type="checkbox"
              checked={isCurrent}
              onChange={() => setIsCurrent((v) => !v)}
              disabled={load}
            />
            Mark as current session
          </label>
          <button
            className="cr_btn cr_btn--primary"
            onClick={registerSession}
            disabled={load || !newSession.trim()}
          >
            Register
          </button>
        </div>
      )}

      {currentSession && (
        <div className="cr_section">
          <div className="cr_section_head">
            <span className="cr_section_label">Current Session</span>
            <span className="cr_session_badge">{currentSession.session}</span>
          </div>
          <Levels
            classes={currentSession.classes || []}
            session={currentSession.session}
          />
        </div>
      )}

      {otherSessions.length > 0 && (
        <div className="cr_section">
          <div className="cr_section_head">
            <span className="cr_section_label">Other Sessions</span>
          </div>
          <div className="cr_session_list">
            {otherSessions.map((s) => (
              <PreviousSession key={s.session} session={s.session} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(Sessions);
